import { useState, type FormEvent } from 'react'
import { InlineLink } from '../../platform/primitives/InlineLink'
import { Button } from '../../platform/primitives/Button'
import { GoogleG } from '../../platform/primitives/GoogleG'
import { Field } from '../../platform/primitives/Field'
import { AlertPanel } from '../../platform/primitives/Panel'
import { useIsCoarsePointer } from '../../platform/responsive/useBreakpoint'
import { emit } from '../../platform/analytics/analytics'
import { analyticsCode, authErrorFor } from '../../auth/errors'
import { AuthFailure } from '../../auth/port'
import { useAuth } from '../../auth/useAuth'
import { useSessionRevoked } from '../../auth/useSessionRevoked'
import type { SignInOutcome } from '../../auth/AuthProvider'
import type { FieldError } from '../../auth/errors'
import { AuthCard, AuthDivider } from './AuthCard'
import { FormError } from '../../auth/FormError'
import './auth.css'

/**
 * M1-R2: every failure lands on the control that caused it.
 *
 * The mapping is `errors.ts`'s job, not this screen's — a form deciding which
 * field a Firebase code belongs to is a decision that would be made again,
 * differently, on sign-up and on reset. Here there is one rule: a `field` of
 * `form` renders once above the button, anything else renders on its input.
 */
export function SignIn() {
  const auth = useAuth()
  const coarse = useIsCoarsePointer()
  const [error, setError] = useState<FieldError | null>(null)
  const [busy, setBusy] = useState(false)

  /**
   * Two sources, one message, resolved during render rather than by an effect.
   *
   * A social redirect fails while this screen does not exist — the document was
   * replaced — so the provider holds that failure and this screen finds it
   * already there on mount. Copying it into local state with a `useEffect`
   * would re-fire on every remount of the route and re-show a message the user
   * has dismissed by acting. The local error wins because it is the newer fact.
   */
  const revoked = useSessionRevoked()
  // The server's own words for why it ended the session — shown until a new one begins.
  const shown: FieldError | null =
    error ??
    auth.redirectError ??
    (revoked === null ? null : { field: 'form', message: revoked, code: 'session-revoked' })

  /**
   * The "too many attempts" panel, keyed on the CODE rather than the field.
   *
   * Every code in `errors.ts`'s form-level bucket resolves to `field: 'form'`
   * — `invalid-credential`, `unauthorized-domain` and the rest — and those
   * keep the plain sentence `FormError` already renders. Only this one code
   * gets the design's alert treatment, so matching the field would also catch
   * the others.
   *
   * **What the panel does not do, and why.** The design draws a live
   * countdown ("paused for 4:52") and a disabled "Locked · 4:52" button.
   * Firebase's `auth/too-many-requests` carries no remaining-time field —
   * there is nothing to count down to, and a timer invented on the client
   * would tell the user exactly when to retry and be wrong. The submit button
   * stays exactly as it is for every other error: enabled, unrelabelled.
   * Firebase itself is the gate; disabling the one control that could prove
   * the lockout has lifted would make this an unrecoverable screen instead of
   * a temporary one.
   */
  const locked = shown?.code === 'auth/too-many-requests'

  async function run(method: 'email' | 'google', action: () => Promise<SignInOutcome>) {
    // Cleared FIRST, and on both sources. A stale message under a field the
    // user has already fixed is worse than no message.
    setError(null)
    auth.clearRedirectError()
    setBusy(true)
    try {
      const outcome = await action()
      // `signin_completed` means a session exists. It does not yet when a
      // second factor was raised — the MFA screen emits the `mfa_used: true`
      // half when the factor clears. Reading `auth.status` here instead, as the
      // plan does, reads this closure's pre-dispatch value and so reported
      // `mfa_used: false` for every challenged sign-in.
      //
      // **Email only.** The provider reports both social arms now, because only
      // it can tell a new account from a returning one (#172) — and emitting
      // here as well double-counted every Google popup. The screen's own test
      // could not see that: `toContainEqual` is satisfied by a duplicate.
      if (method === 'email' && outcome === 'complete') {
        emit('signin_completed', { method, mfa_used: false })
      }
    } catch (thrown) {
      // Nothing types what comes through the port, so an unmapped throw gets
      // the unclassified message rather than a guessed field — `authErrorFor`
      // refuses to guess, which is the behaviour that matters here.
      const detail = thrown instanceof AuthFailure ? thrown.detail : authErrorFor('unexpected')
      setError(detail)
      emit('signin_failed', { method, error_code: analyticsCode(detail.code) })
    } finally {
      setBusy(false)
    }
  }

  function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    // `FormData` rather than two controlled inputs: nothing on this screen
    // reacts to a keystroke, so state per character would buy re-renders and
    // no behaviour. The password never enters React state at all.
    const form = new FormData(event.currentTarget)
    const email = String(form.get('email') ?? '')
    const password = String(form.get('password') ?? '')
    void run('email', () => auth.signIn(email, password))
  }

  return (
    <AuthCard
      title="Welcome back"
      subtitle="Sign in to your household"
      /* The design puts this OUTSIDE the card — see `AuthCard`. */
      foot={<>New here? <InlineLink to="/signup">Create an account</InlineLink></>}
    >
      {/* `noValidate`, so the browser's own bubble does not pre-empt the
          messages M1-R2 asks for — two validation systems disagreeing on one
          form is worse than either alone. */}
      <form className="auth-form" onSubmit={onSubmit} noValidate>
        <Field
          size="form"
          label="Email"
          name="email"
          type="email"
          autoComplete="username"
          error={shown?.field === 'email' ? shown.message : undefined}
        />
        <Field
          size="form"
          label="Password"
          name="password"
          type="password"
          autoComplete="current-password"
          error={shown?.field === 'password' ? shown.message : undefined}
          /* On the label row, where the design has it. The word is "Forgot?"
             rather than the old "Forgot your password?" because the row is
             shared with the label and the question is already scoped by it.
             `InlineLink` rather than bare text — rule 11 wants a standalone
             link to carry more than colour. */
          labelAction={<InlineLink to="/forgot-password">Forgot?</InlineLink>}
        />
        {locked ? (
          /* The comp draws this panel at the TOP of the card, above the
             fields. It stays here instead, in the slot every other
             form-level failure already uses — M1-R2's invariant is "a
             `field` of `form` renders once above the button", and moving
             just this one code to a different position would make where a
             failure lands depend on which code it was, which is the thing
             that rule exists to prevent.

             `AlertPanel` (`platform/primitives/Panel`) already IS
             `background: var(--neg-soft); border-color: var(--neg)` — the
             two colours the design draws this panel in — so nothing here
             invents a new fill. Its default box (a 20px-padded column, for
             Async's error state) is overridden inline to the comp's row
             layout, the same way Async.tsx overrides its `gap`: a caller's
             `style` is the documented escape for a one-off box on a shared
             surface, not a reason to fork the surface itself. */
          <AlertPanel style={{ flexDirection: 'row', gap: 11, padding: 14, borderRadius: 12 }}>
            <span className="auth-alert__badge" aria-hidden="true">
              !
            </span>
            {/* One region, not two: a screen reader gets the title and the
                cause together rather than a bare "!" followed by silence. */}
            <div className="auth-alert__body" role="alert">
              <span className="auth-alert__title">Too many attempts</span>
              <p className="auth-alert__text">
                This account is temporarily locked after repeated failed sign-ins, to keep
                it safe. Try again shortly, or reset your password below.
              </p>
            </div>
          </AlertPanel>
        ) : (
          <FormError error={shown} />
        )}
        <Button type="submit" variant="primary" size="form" disabled={busy}>
          {busy ? 'Signing in…' : 'Sign in'}
        </Button>
        {/* The one action that actually resolves this, kept from the comp.
            "Sign in with a passkey" is dropped rather than transcribed —
            passkeys are not implemented, and Google already took the
            second-factor slot the comp draws them in (see below). */}
        {locked ? (
          <p className="auth-alert-actions">
            <InlineLink to="/forgot-password">Reset my password instead</InlineLink>
          </p>
        ) : null}
      </form>

      <AuthDivider />

      {/* The design's second slot is "Use a passkey". Google takes it: the
          passkey endpoints are backend-only (#189) and parked behind the
          decision to adopt the backend MFA API at all (#188), so that control
          has nothing to call. Google is what this app actually signs in with,
          and it is the same shape — one full-width secondary under the rule.

          `outline`, not a second `primary`: CLAUDE.md allows one lime surface
          per screen and the email submit is it. */}
      <Button
        variant="outline"
        size="form"
        className="btn--google"
        disabled={busy}
        // M1-R23: popups are unreliable on mobile browsers, and pre-login is
        // the one genuinely mobile-first area of the app. `useIsCoarsePointer`
        // is the single source — a component may not read window.innerWidth.
        onClick={() => void run('google', () => auth.signInWithGoogle(coarse ? 'redirect' : 'popup'))}
      >
        {/* States its reason while disabled, like every other control here. A
            control that greys out and keeps its resting label reads as broken,
            and on the redirect path there is a real wait to explain. */}
        <>
          <GoogleG />
          {busy ? 'Opening Google…' : 'Continue with Google'}
        </>
      </Button>

      {/* Stated rather than silently missing. Apple sign-in needs C8 — a
          Services ID under the same primary App ID as the iOS bundle — so an
          Apple-only iOS user cannot reach the web app at all. Saying so beats
          leaving them to conclude their account is gone. */}
      <p className="auth-card__note">
        Sign in with Apple isn't available on the web yet. If that's how you signed up, use the
        iPhone app for now.
      </p>
    </AuthCard>
  )
}
